import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Avatar,
  Button,
  Skeleton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ArrowBack,
  AccountBalanceWallet,
  LocationOn,
  People,
  BusinessCenter,
  TrendingUp,
  Star,
  Refresh,
  Settings,
} from '@mui/icons-material';
import { moduleService } from '../services';
import { Line } from 'recharts';
import { LineChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ModuleLayout } from '../components/Layout/ModuleLayout';
import { colors } from '../theme/theme';

interface ModuleDashboardData {
  module: {
    id: number;
    name: string;
    description: string;
    icon: string | null;
    module_type: {
      id: number;
      name: string;
    };
  };
  stats: {
    totalRevenue: number;
    collectionPoints: number;
    uniqueUsers: number;
    serviceCount: number;
    successRate: number;
  };
  monthlyRevenue: Array<{
    month: string;
    revenue: number;
  }>;
  bestPerformingServices: Array<{
    name: string;
    count: number;
    revenue: number;
  }>;
  recentTransactions: Array<{
    id: number;
    transaction_reference: string;
    service_name: string;
    user: string;
    status: string;
    date: string;
    total: number;
  }>;
}

export const ModuleDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<ModuleDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchModuleDashboard();
  }, [id]);

  const fetchModuleDashboard = async () => {
    try {
      setLoading(true);
      const response = await moduleService.getModuleDashboard(Number(id));
      if (response.status === 'success' && response.data) {
        setData(response.data);
      } else {
        setError('Failed to load module dashboard');
      }
    } catch (err: any) {
      console.error('Error fetching module dashboard:', err);
      setError(err.message || 'Failed to load module dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        {/* Header Skeleton */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width={300} height={40} />
            <Skeleton width={400} />
          </Box>
        </Box>

        {/* KPI Cards Skeleton */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Grid key={i} size={{ xs: 12, md: i === 1 ? 12 : 6, lg: i === 1 ? 4 : 2.4 }}>
              <Card>
                <CardContent>
                  <Skeleton width="60%" />
                  <Skeleton width="80%" height={60} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Module not found'}
        </Alert>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/modules')}>
          Back to Modules
        </Button>
      </Box>
    );
  }

  return (
    <ModuleLayout module={data.module} moduleId={id!}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 4,
            animation: 'slideInDown 0.5s ease-out',
            '@keyframes slideInDown': {
              from: { opacity: 0, transform: 'translateY(-20px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', mb: 1 }}>
              Dashboard Overview
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {data.module.description}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <IconButton
              onClick={fetchModuleDashboard}
              sx={{
                bgcolor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: '#f8fafc',
                  borderColor: '#cbd5e1',
                },
              }}
            >
              <Refresh />
            </IconButton>
          </Box>
        </Box>

      {/* KPI Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Revenue - Primary Gradient */}
        <Grid size={{ xs: 12, md: 12, lg: 4 }}>
          <Card
            sx={{
              background: colors.gradients.primary,
              color: 'white',
              borderRadius: 4,
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              animation: 'fadeInUp 0.6s ease-out',
              '@keyframes fadeInUp': {
                from: { opacity: 0, transform: 'translateY(20px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: colors.shadows.vibrantHover,
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                bottom: -20,
                right: -20,
                width: 150,
                height: 150,
                opacity: 0.15,
                background: 'white',
                borderRadius: '50%',
              },
            }}
          >
            <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 4 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255, 255, 255, 0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AccountBalanceWallet sx={{ fontSize: 28 }} />
                </Box>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  fontWeight: 700,
                  mb: 1,
                  display: 'block',
                }}
              >
                Total Revenue
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {formatCurrency(data.stats.totalRevenue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Stats Grid */}
        <Grid size={{ xs: 12, md: 12, lg: 8 }}>
          <Grid container spacing={3}>
            {/* Collection Points */}
            <Grid size={{ xs: 12, sm: 6, lg: 6 }}>
              <Card
                sx={{
                  borderRadius: 4,
                  height: '100%',
                  transition: 'all 0.3s ease',
                  animation: 'fadeInUp 0.7s ease-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: colors.shadows.vibrant,
                  },
                }}
              >
                <CardContent sx={{ p: 4, display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: 'rgba(13, 202, 240, 0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 3,
                    }}
                  >
                    <LocationOn sx={{ fontSize: 28, color: '#0dcaf0' }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      Collection Points
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937' }}>
                      {data.stats.collectionPoints}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Active Users */}
            <Grid size={{ xs: 12, sm: 6, lg: 6 }}>
              <Card
                sx={{
                  borderRadius: 4,
                  height: '100%',
                  transition: 'all 0.3s ease',
                  animation: 'fadeInUp 0.8s ease-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: colors.shadows.vibrant,
                  },
                }}
              >
                <CardContent sx={{ p: 4, display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: colors.primary[50],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 3,
                    }}
                  >
                    <People sx={{ fontSize: 28, color: colors.primary.main }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      Active Users
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937' }}>
                      {data.stats.uniqueUsers.toLocaleString()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Services */}
            <Grid size={{ xs: 12, sm: 6, lg: 6 }}>
              <Card
                sx={{
                  borderRadius: 4,
                  height: '100%',
                  transition: 'all 0.3s ease',
                  animation: 'fadeInUp 0.9s ease-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: colors.shadows.vibrant,
                  },
                }}
              >
                <CardContent sx={{ p: 4, display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: 'rgba(46, 196, 182, 0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 3,
                    }}
                  >
                    <BusinessCenter sx={{ fontSize: 28, color: '#2ec4b6' }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      Services
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937' }}>
                      {data.stats.serviceCount}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Success Rate */}
            <Grid size={{ xs: 12, sm: 6, lg: 6 }}>
              <Card
                sx={{
                  borderRadius: 4,
                  height: '100%',
                  transition: 'all 0.3s ease',
                  animation: 'fadeInUp 1s ease-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: colors.shadows.vibrant,
                  },
                }}
              >
                <CardContent sx={{ p: 4, display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: 'rgba(108, 117, 125, 0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 3,
                    }}
                  >
                    <TrendingUp sx={{ fontSize: 28, color: '#6c757d' }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      Success Rate
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937' }}>
                      {data.stats.successRate}%
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Charts and Tables */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Revenue Trend Chart */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card
            sx={{
              borderRadius: 4,
              height: '100%',
              animation: 'fadeInLeft 0.8s ease-out',
              '@keyframes fadeInLeft': {
                from: { opacity: 0, transform: 'translateX(-20px)' },
                to: { opacity: 1, transform: 'translateX(0)' },
              },
            }}
          >
            <CardHeader
              title={
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937' }}>
                    Revenue Trend
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Monthly performance overview
                  </Typography>
                </Box>
              }
              sx={{ pb: 0 }}
            />
            <CardContent>
              {data.monthlyRevenue.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={data.monthlyRevenue}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={colors.primary.main} stopOpacity={0.5} />
                        <stop offset="95%" stopColor={colors.primary.dark} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" stroke="#6b7280" style={{ fontSize: 12 }} />
                    <YAxis
                      stroke="#6b7280"
                      style={{ fontSize: 12 }}
                      tickFormatter={(value) => {
                        if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                        if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
                        return value;
                      }}
                    />
                    <Tooltip
                      formatter={(value: any) => formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke={colors.primary.main}
                      strokeWidth={3}
                      dot={{ fill: colors.primary.dark, r: 4 }}
                      activeDot={{ r: 6 }}
                      fill="url(#colorRevenue)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                  <Typography variant="body2">No sufficient revenue data to display trend.</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Top Services */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card
            sx={{
              borderRadius: 4,
              height: '100%',
              animation: 'fadeInRight 0.8s ease-out',
              '@keyframes fadeInRight': {
                from: { opacity: 0, transform: 'translateX(20px)' },
                to: { opacity: 1, transform: 'translateX(0)' },
              },
            }}
          >
            <CardHeader
              title={
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937' }}>
                    Top Services
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    By total revenue generation
                  </Typography>
                </Box>
              }
              sx={{ pb: 0 }}
            />
            <CardContent sx={{ p: 0 }}>
              {data.bestPerformingServices.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableBody>
                      {data.bestPerformingServices.map((service, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            '&:hover': {
                              bgcolor: colors.primary[50],
                            },
                          }}
                        >
                          <TableCell sx={{ borderBottom: 'none', py: 2, pl: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: '50%',
                                  bgcolor: colors.primary[50],
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mr: 2,
                                }}
                              >
                                <Star sx={{ fontSize: 20, color: colors.primary.main }} />
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1f2937' }}>
                                  {service.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {service.count} transactions
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderBottom: 'none', py: 2, pr: 3, textAlign: 'right' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#2ec4b6' }}>
                              {formatCurrency(service.revenue)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                  <Typography variant="body2">No service data available yet.</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Latest Transactions */}
      <Card
        sx={{
          borderRadius: 4,
          animation: 'fadeInUp 1s ease-out',
        }}
      >
        <CardHeader
          title={
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937' }}>
                Latest Transactions
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Recent activity for this module
              </Typography>
            </Box>
          }
        />
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f9fafb' }}>
                  <TableCell sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', color: '#6b7280' }}>
                    Service Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', color: '#6b7280' }}>
                    User
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', color: '#6b7280' }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', color: '#6b7280' }}>
                    Date
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      textAlign: 'right',
                    }}
                  >
                    Amount
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.recentTransactions.length > 0 ? (
                  data.recentTransactions.map((transaction) => (
                    <TableRow
                      key={transaction.id}
                      sx={{
                        '&:hover': {
                          bgcolor: colors.primary[50],
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1f2937' }}>
                          {transaction.service_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: 12, bgcolor: '#6c757d' }}>
                            {transaction.user.charAt(0)}
                          </Avatar>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {transaction.user}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.status}
                          color={getStatusColor(transaction.status)}
                          size="small"
                          sx={{
                            borderRadius: 2,
                            fontWeight: 600,
                            textTransform: 'capitalize',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                          {transaction.date}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1f2937' }}>
                          {formatCurrency(transaction.total)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                      <Typography variant="body2">No transactions found for this module yet.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      </Box>
    </ModuleLayout>
  );
};
